import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, Info, Bell, Check, Clock, Trash2, CalendarPlus, UserRoundPlus, CreditCard } from 'lucide-react';
import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';
import { useNotifications } from '@/contexts/NotificationsContext'; // Usa il context reale

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { notifications, markAllAsRead, markAsRead } = useNotifications(); // Dati reali
    const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
    const [visibleCount, setVisibleCount] = useState(20);

    // Filter Logic
    const filteredNotifications = notifications.filter((n: any) => {
        if (activeTab === 'unread') return !n.read;
        return true;
    });

    // Helper: Formatta data relativa (es. "5 min fa")
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffMin < 1) return 'Adesso';
        if (diffMin < 60) return `${diffMin} min fa`;
        if (diffHour < 24) return `${diffHour} ore fa`;
        return `${diffDay} giorni fa`;
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'free_trial': return <Clock size={20} className="text-blue-500" />;
            case 'booking': return <CalendarPlus size={20} className="text-purple-500" />;
            case 'client': return <UserRoundPlus size={20} className="text-green-500" />;
            case 'billing': return <CreditCard size={20} className="text-red-500" />;
            case 'success': return <CheckCircle2 size={20} className="text-green-500" />;
            case 'alert': return <AlertTriangle size={20} className="text-red-500" />;
            default: return <Info size={20} className="text-blue-500" />;
        }
    };

    const getBgColor = (type: string) => {
        switch (type) {
            case 'success': return 'bg-green-100';
            case 'alert': return 'bg-red-100';
            case 'booking': return 'bg-purple-100';
            case 'client': return 'bg-green-100';
            case 'billing': return 'bg-red-100';
            default: return 'bg-blue-100';
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-2xl h-[80vh] flex flex-col"
            headerClassName="bg-white pb-0"
            headerContent={
                <div className="">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <Bell className="text-clip-500" /> {t('notifications.title')}
                            </h2>
                            <p className="text-gray-500 text-sm mt-1">Resta aggiornato su ciò che accade nel salone.</p>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-6 border-b border-gray-100">
                        <button
                            onClick={() => {
                                setActiveTab('all');
                                setVisibleCount(20);
                            }}
                            className={`pb-3 text-sm font-bold transition-all relative ${activeTab === 'all' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            Tutte
                            {activeTab === 'all' && <motion.div layoutId="notifTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />}
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('unread');
                                setVisibleCount(20);
                            }}
                            className={`pb-3 text-sm font-bold transition-all relative ${activeTab === 'unread' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            Da Leggere
                            {notifications.filter((n: any) => !n.read).length > 0 && (
                                <span className="ml-2 bg-clip-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                    {notifications.filter((n: any) => !n.read).length}
                                </span>
                            )}
                            {activeTab === 'unread' && <motion.div layoutId="notifTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />}
                        </button>
                    </div>
                </div>
            }
        >
            <div className="h-full overflow-y-auto custom-scrollbar space-y-3">
                <AnimatePresence mode="popLayout">
                    {filteredNotifications.length > 0 ? (
                        filteredNotifications.slice(0, visibleCount).map((notif: any) => (
                            <motion.div
                                layout
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                key={notif.id}
                                onClick={() => markAsRead(notif.id)}
                                className={`group relative p-4 rounded-2xl border transition-all cursor-pointer flex gap-4 ${notif.read
                                    ? 'bg-white border-transparent hover:border-gray-200 hover:bg-gray-50'
                                    : 'bg-orange-50/40 border-orange-100 hover:bg-orange-50'
                                    }`}
                            >
                                {/* Icon */}
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${getBgColor(notif.type)}`}>
                                    {getIcon(notif.type)}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start">
                                        <h4 className={`text-sm ${notif.read ? 'font-semibold text-gray-900' : 'font-bold text-gray-900'}`}>
                                            {notif.title}
                                        </h4>
                                        <span className="text-[10px] text-gray-400 font-medium flex items-center gap-1 shrink-0 bg-white/50 px-2 py-0.5 rounded-md">
                                            <Clock size={10} /> {formatDate(notif.created_at)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{notif.message}</p>
                                </div>

                                {/* Unread Indicator */}
                                {/* {!notif.read && (
                                    <div className="absolute top-4 right-4 w-2 h-2 bg-clip-500 rounded-full shadow-sm"></div>
                                )} */}
                            </motion.div>
                        ))
                    ) : (
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center py-20 text-gray-400"
                        >
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <Bell size={24} className="text-gray-300" />
                            </div>
                            <p className="text-sm font-medium">Nessuna notifica trovata.</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {filteredNotifications.length > visibleCount && (
                    <div className="flex justify-center pt-4 pb-6">
                        <PrimaryButton
                            variant="secondary"
                            label={t('common.show_more')}
                            onClick={() => setVisibleCount((prev) => prev + 20)}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default NotificationsModal;